import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createListCollection } from "@chakra-ui/react";
import { CandidateService } from "@/services/CandidateService";
import InteractionList from "../InteractionList";

const CandidateInteractionsTab = ({ candidateId }) => {
    const { t } = useTranslation("models");
    const navigate = useNavigate();
    const { id: modelId, recruitmentId } = useParams();

    const [conversations, setConversations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalCount, setTotalCount] = useState(0);
    const [statusFilter, setStatusFilter] = useState("");
    const [sortBy, setSortBy] = useState("createdat");
    const [sortDir, setSortDir] = useState("desc");
    const [showFavoriteOnly, setShowFavoriteOnly] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const statusCollection = createListCollection({
        items: [
            { label: t("conversationStatus.all"), value: "" },
            { label: t("conversationStatus.inprogress"), value: "InProgress" },
            { label: t("conversationStatus.finished"), value: "Finished" },
            { label: t("conversationStatus.holded"), value: "Holded" },
            { label: t("conversationStatus.cancelled"), value: "Cancelled" },
            { label: t("conversationStatus.qualifiedfornextstage"), value: "QualifiedForNextStage" },
            { label: t("conversationStatus.furtherselection"), value: "FurtherSelection" },
            { label: t("conversationStatus.readytohire"), value: "ReadyToHire" },
            { label: t("conversationStatus.hired"), value: "Hired" },
            { label: t("conversationStatus.rejected"), value: "Rejected" },
            { label: t("conversationStatus.candidateresigned"), value: "CandidateResigned" },
        ],
    });

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        setIsLoading(true);
        CandidateService.getInteractions(
            candidateId,
            page,
            pageSize,
            statusFilter || null,
            sortBy,
            sortDir,
            showFavoriteOnly || null,
            debouncedSearch || null,
        ).then((data) => {
            setConversations(data?.items ?? []);
            setTotalCount(data?.totalCount ?? 0);
        }).finally(() => setIsLoading(false));
    }, [candidateId, page, pageSize, statusFilter, sortBy, sortDir, showFavoriteOnly, debouncedSearch]);

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortDir(d => d === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("desc");
        }
        setPage(1);
    };

    const handleRowClick = (conv) => {
        navigate(`/models/${modelId}/recruitments/${recruitmentId}/conversations/${conv.id}`);
    };

    return (
        <InteractionList
            conversations={conversations}
            isLoading={isLoading}
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={(ps) => { setPageSize(ps); setPage(1); }}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
            showFavoriteOnly={showFavoriteOnly}
            onFavoriteToggle={() => { setShowFavoriteOnly(v => !v); setPage(1); }}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            statusFilter={statusFilter}
            onStatusChange={(val) => { setStatusFilter(val); setPage(1); }}
            statusCollection={statusCollection}
            onRowClick={handleRowClick}
        />
    );
};

export default CandidateInteractionsTab;
